/**
 * cl-core/names — 이름 정규화 · 글자 수 (CL fix_2 · 2026-10-15). 순수 TS.
 */

/**
 * 표시 이름 정규화.
 *  - 이름 사이 쉼표(공백 없음·전각 포함)는 가운뎃점 — 「남일,흥아아파트」 → 「남일·흥아아파트」
 *  - 연속 공백 1칸 · 앞뒤 공백 제거
 * ⚠️ 쉼표 뒤에 공백이 있는 「A, B」 는 나열 문장일 수 있어 건드리지 않는다.
 */
export function normalizeSiteName(name: string | null | undefined): string {
  let s = String(name ?? '');
  // 이름 사이 쉼표만. 숫자-쉼표-숫자(천 단위 「2,180세대」)와 쉼표+공백 나열(「A, B」)은 건드리지 않는다.
  s = s.replace(/([가-힣A-Za-z0-9])[,，、](?=([가-힣A-Za-z0-9]))/g, (m: string, a: string, b: string) =>
    /[0-9]/.test(a) && /[0-9]/.test(b) ? m : `${a}·`);
  s = s.replace(/\s+/g, ' ').trim();
  return s;
}

/** 코드 포인트 기준 글자 수(서로게이트 쌍 1자). title 길이 판정용. */
export function charLen(s: string | null | undefined): number {
  return Array.from(String(s ?? '')).length;
}

/* ───────── 단지명 해석(fix_7 §3) ───────── */

export interface DisplayNameInput {
  /** apt_sites.name */
  name: string | null | undefined;
  /** apt_sites.display_name */
  display_name?: string | null;
  /** apt_sites.site_type ('redevelopment' 이면 사업유형 재개발/재건축) */
  site_type?: string | null;
  /** 정비 원장 project_type(재건축 판별) */
  project_type?: string | null;
}

export type DisplayNameSource = 'brand' | 'display_name' | 'district' | 'name';

export interface ResolvedDisplayName {
  /** 화면·제목에 쓸 단지명 */
  name: string;
  /** 브랜드명(있으면) */
  brand: string | null;
  /** 구역명(「가야1구역」 · 있으면) */
  district: string | null;
  /** 구역 약칭+사업유형(「가야1 재개발」 · 정비 현장이고 구역명이 있을 때) */
  districtLabel: string | null;
  source: DisplayNameSource;
}

/** 브랜드가 아닌 «일반·지역·구역 표기» 판정. */
const GENERIC_RE = /(공동주택|구역|지구|재개발|재건축|정비|도시개발|번지|일원|\d+\s*BL\b|BL$|블록)/i;

function sanitize(s: string | null | undefined): string {
  return normalizeSiteName(String(s ?? '')).trim();
}

function parenSplit(s: string): { outer: string; inner: string | null } {
  const m = /^(.*?)\s*[(（]([^()（）]+)[)）]\s*$/.exec(s);
  return m && m[1].trim() ? { outer: m[1].trim(), inner: m[2].trim() } : { outer: s, inner: null };
}

/** 「서금사재정비촉진A구역 재개발」·「가음4구역」·「(가야1구역)」 → 구역명. */
function districtOf(...cands: Array<string | null | undefined>): string | null {
  for (const c of cands) {
    const s = String(c ?? '');
    const m = /([가-힣A-Za-z0-9]+?(?:재정비촉진)?[0-9A-Za-z]*-?[0-9A-Za-z]*구역)/.exec(s);
    if (m) return m[1];
  }
  return null;
}

/** 구역 약칭: 「서금사재정비촉진6구역」→「서금사6」, 「가야1구역」→「가야1」. */
export function districtShort(district: string): string {
  return district.replace(/재정비촉진/g, '').replace(/주택재개발|주택재건축/g, '').replace(/구역$/, '').trim();
}

/**
 * 단지명 해석 — 우선순위: 브랜드명 → display_name → 구역 약칭+사업유형 → name.
 *  · 브랜드 출처: display_name 「브랜드 — 일반명」의 왼쪽 · name 「브랜드(○○구역)」의 바깥(일반·구역 표기가 아닐 때).
 *  · display_name 이 「— 」 꼴이 아니면 그대로(종전 표시명).
 *  · 둘 다 없고 정비 현장이며 구역명이 보이면 「{약칭} {재개발|재건축}」.
 *  · 그 밖은 name.
 * ⛔ 지어내지 않는다 — 브랜드는 위 두 출처에 «쓰여 있을 때만».
 */
export function resolveDisplayName(i: DisplayNameInput): ResolvedDisplayName {
  const name = sanitize(i.name);
  const disp = sanitize(i.display_name);
  const isRedev = i.site_type === 'redevelopment';
  let brand: string | null = null;

  const dash = /^(.+?)\s+[—–-]\s+(.+)$/.exec(disp);
  if (dash && !GENERIC_RE.test(dash[1])) brand = dash[1].trim();
  const ps = parenSplit(name);
  if (!brand && ps.inner && /구역$/.test(ps.inner) && !GENERIC_RE.test(ps.outer)) brand = ps.outer;

  const district = (ps.inner && /구역$/.test(ps.inner) ? ps.inner : null) ?? districtOf(dash ? dash[2] : null, name, disp);
  const typeWord = /재건축/.test(`${i.project_type ?? ''} ${name} ${disp}`) ? '재건축' : '재개발';
  const short = district ? districtShort(district) : '';
  // 약칭에 한글이 없으면(「2구역」→「2」) 약칭으로 쓰지 않는다 — 「2 재개발」 같은 이름을 만들지 않는다.
  const districtLabel = isRedev && short && /[가-힣]/.test(short) ? `${short} ${typeWord}` : null;

  if (brand) return { name: brand, brand, district, districtLabel, source: 'brand' };
  if (disp && !dash) return { name: disp, brand: null, district, districtLabel, source: 'display_name' };
  if (districtLabel) return { name: districtLabel, brand: null, district, districtLabel, source: 'district' };
  return { name: name || disp, brand: null, district, districtLabel, source: 'name' };
}

/* ───────── 지역·유형어 정규화(hc3 H8 db6 · L4 — 단일 출처) ───────── */

/**
 * 시도 17개 짧은 이름 «단일 원본» — apt_sites.region 등 DB 키와 같다(2026-10-24 SELECT distinct region 실측 17개 ·
 *   통합 표기 「전남광주」 17행은 원천별 예외라 여기 넣지 않는다). 순서 = 행정 코드 관례(특·광역시 → 도).
 *   공개 통로: lib/regions 의 REGIONS(튜플 타입) · SIDO_NAMES(readonly string[]).
 */
/** 부울경(부산·울산·경남) — 우선 지역 «단일 원본». lib/region/buulgyeong · cl-core/headline 이 이것을 다시 내보낸다. */
export const BUULGYEONG_SIDO: readonly string[] = ['부산', '울산', '경남'];

export const SIDO_17 = ['서울', '부산', '대구', '인천', '광주', '대전', '울산', '세종', '경기', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'] as const;

/**
 * B4(AUDIT_1030) — 화면마다 다른 «앞머리 순서» 를 사본 없이 만든다: head 를 먼저, 나머지는 SIDO_17 순서.
 *   예) ['서울','경기','인천'] → 수도권 먼저(cafe 메뉴·cj 표 순서). head 에 17개 밖 이름은 무시.
 */
export function sidoOrderWithHead(head: readonly string[]): string[] {
  const all = SIDO_17 as readonly string[];
  const h = head.filter((x, i) => all.includes(x) && head.indexOf(x) === i);
  return [...h, ...all.filter((x) => !h.includes(x))];
}

/** 수도권(서울·경기·인천) 먼저 순서 — cafe/contract SIDO_LIST · cj/parts SIDO_ORDER 의 정본. */
export const SIDO_17_CAPITAL_FIRST: readonly string[] = sidoOrderWithHead(['서울', '경기', '인천']);

/* ── 광주·전남 통합 표기(「전남광주」) — 원천별 규칙의 «재료» 단일 출처(B4). 병합 «여부» 는 여전히 호출부가 정한다. ── */

/** 통합 시·도 짧은 이름(regions 표 code 12 · MOLIT 2026-07~ 집계 행). */
export const MERGED_SIDO_JN_GJ = '전남광주';
/** 통합 표기의 구성 시·도. */
export const JN_GJ_MEMBERS: readonly string[] = ['광주', '전남'];
/** 통합 표기의 변형들(주소 머리·별칭). */
export const MERGED_SIDO_ALIASES: readonly string[] = [MERGED_SIDO_JN_GJ, '전남광주통합특별시', '광주전남'];

/** 시도 → DB 조회 후보. 광주·전남은 통합 행(「전남광주」)도 함께 본다. 그 밖은 [r]. */
export function withMergedSido(r: string): string[] {
  return JN_GJ_MEMBERS.includes(r) ? [r, MERGED_SIDO_JN_GJ] : [r];
}

/** 통합 표기(「전남광주」) → 구성 시·도 ['광주','전남']. 통합 표기가 아니면 null. */
export function mergedSidoMembers(r: string): string[] | null {
  return r === MERGED_SIDO_JN_GJ ? [...JN_GJ_MEMBERS] : null;
}

/** 광주·전남·통합 표기 변형 중 하나인가(시군구가 속한 쪽으로 풀어야 하는 표기군). */
export function isJnGjFamily(s: string): boolean {
  return JN_GJ_MEMBERS.includes(s) || MERGED_SIDO_ALIASES.includes(s);
}

/**
 * 정식·약칭 한글 시도 이름 → 시도 짧은 이름(서울·부산 … 세종·제주). 단일 출처.
 * ⚠️ 광주·전남 통합(전남광주) 같은 «통계 행 병합» 은 여기서 하지 않는다 — 그건 원천별 규칙이라
 *    호출부(apt/unsold-month 의 MOLIT 표)가 따로 덮어쓴다.
 */
export const SIDO_SHORT_BY_NAME: Readonly<Record<string, string>> = {
  서울특별시: '서울', 서울시: '서울', 부산광역시: '부산', 부산시: '부산', 대구광역시: '대구', 대구시: '대구',
  인천광역시: '인천', 인천시: '인천', 광주광역시: '광주', 대전광역시: '대전', 대전시: '대전', 울산광역시: '울산', 울산시: '울산',
  세종특별자치시: '세종', 세종시: '세종', 경기도: '경기', 강원도: '강원', 강원특별자치도: '강원', 충청북도: '충북', 충청남도: '충남',
  전라북도: '전북', 전북특별자치도: '전북', 전라남도: '전남', 경상북도: '경북', 경상남도: '경남', 제주특별자치도: '제주', 제주도: '제주',
};

const SIDO_SHORT_SET: ReadonlySet<string> = new Set(Object.keys(SIDO_SHORT_BY_NAME).map((k) => SIDO_SHORT_BY_NAME[k]));

/** 시도 표기 → 짧은 이름. 이미 짧은 이름이면 그대로, 모르면 null. */
export function sidoShortName(s: string | null | undefined): string | null {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  if (SIDO_SHORT_SET.has(t)) return t;
  return SIDO_SHORT_BY_NAME[t] ?? null;
}

export interface PlaceParts {
  sido?: string | null;
  sigungu?: string | null;
  dong?: string | null;
}

/**
 * 「시도 시군구 동」 한 줄 — 순서 고정(시도 → 시군구 → 동) · 중복 제거.
 *  - 시도는 짧은 이름으로(「인천광역시」 → 「인천」).
 *  - 시군구가 시도 자체를 가리키면(세종특별자치시·세종시) 뺀다 — 「세종 세종특별자치시」 를 만들지 않는다.
 *  - 이미 앞에 나온 조각은 다시 쓰지 않는다.
 */
export function joinPlace(p: PlaceParts): string {
  const sido = sidoShortName(p.sido) ?? String(p.sido ?? '').trim();
  const out: string[] = [];
  const push = (v: string | null | undefined) => {
    const t = String(v ?? '').replace(/\s+/g, ' ').trim();
    if (!t || out.indexOf(t) >= 0) return;
    out.push(t);
  };
  push(sido);
  const sg = String(p.sigungu ?? '').trim();
  if (sg && !(sido && sidoShortName(sg) === sido)) push(sg);
  push(p.dong);
  return out.join(' ');
}

/*
 * 유형어(재개발·재건축 …) 중복 방지 — 단일 출처. apt/title-tail.ts ⑨·⑬③ 에서 옮겨 왔다(그쪽은 재수출).
 *   「서금사재정비촉진A구역 재개발」 + 「재개발 · 진행현황」 → 「서금사재정비촉진A구역 재개발 · 진행현황」
 *   「센텀1구역 재건축(재송코오롱아파트)」 + 「재개발」 → 「센텀1구역 재건축(재송코오롱아파트)」
 */
const TYPE_WORD = '(?:재개발|재건축|재정비|주거환경개선|소규모재건축|가로주택(?:정비)?|리모델링)(?:정비)?(?:사업)?';
const NAME_ENDS_TYPE_RE = new RegExp(`${TYPE_WORD}$`);
const TAIL_LEADS_TYPE_RE = new RegExp(`^${TYPE_WORD}(?:\\s*·\\s*|\\s+|$)`);

export function nameEndsWithTypeWord(name: string | null | undefined): boolean {
  const core = String(name ?? '').trim().replace(/\s*[(（][^)）]*[)）]\s*$/, '').trim();
  return NAME_ENDS_TYPE_RE.test(core);
}

/** `${name} ${word}` — 이름이 유형어로 끝나면 word 를 붙이지 않는다. */
export function withTypeWord(name: string, word: string | null | undefined): string {
  const w = String(word ?? '').trim();
  if (!w) return name;
  if (nameEndsWithTypeWord(name) && TAIL_LEADS_TYPE_RE.test(w)) return name;
  return `${name} ${w}`;
}

/** `${name} ${tail}` — 이름이 유형어로 끝나고 꼬리가 유형어로 시작하면 꼬리의 유형어만 떼고 「 · 나머지」로 잇는다. */
export function withTitleTail(name: string, tail: string | null | undefined): string {
  const t = String(tail ?? '').trim();
  if (!t) return name;
  if (nameEndsWithTypeWord(name) && TAIL_LEADS_TYPE_RE.test(t)) {
    const rest = t.replace(TAIL_LEADS_TYPE_RE, '').trim();
    return rest ? `${name} · ${rest}` : name;
  }
  return `${name} ${t}`;
}

/**
 * 저장된 문장의 «유형어 연속» 을 하나로 접는다(공백으로 이웃한 경우만).
 *   「여의도시범아파트 재건축 재건축 분석 —」 → 「여의도시범아파트 재건축 분석 —」
 * ⚠️ 가운뎃점·쉼표로 이은 나열(「재개발·재건축 사업」)은 건드리지 않는다.
 */
const DUP_TYPE_RE = new RegExp(`(${TYPE_WORD})((?:[(（][^)）]*[)）])?)\\s+${TYPE_WORD}(?=[\\s.,·—\\-)]|$)`, 'g');
export function collapseTypeWordDup(text: string | null | undefined): string {
  return String(text ?? '').replace(DUP_TYPE_RE, '$1$2');
}

/**
 * 제목 조립용 유형어 정리(hc3 L4·db6): collapseTypeWordDup + 「(소규모) 소규모재건축」 같은
 * «괄호 조각이 다음 단어 머리와 같은» 군더더기 제거 + 공백 1칸.
 *   「반송동 재건축 재건축 진행현황」 → 「반송동 재건축 진행현황」
 *   「(소규모) 소규모재건축 재건축」 → 「소규모재건축」
 */
export function dedupeTypeWords(s: string | null | undefined): string {
  const toks = String(s ?? '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const kept: string[] = [];
  for (let k = 0; k < toks.length; k++) {
    const paren = /^[(（]([^()（）]+)[)）]$/.exec(toks[k]);
    if (paren && k + 1 < toks.length && toks[k + 1] !== paren[1] && toks[k + 1].indexOf(paren[1]) === 0) continue;
    kept.push(toks[k]);
  }
  return collapseTypeWordDup(kept.join(' '));
}

/* ───────── live1-d — 주소 시도 중복 · 이름+유형 배지 반복 ───────── */

/**
 * 「{시도} {주소}」 한 줄 — 주소가 이미 시도로 시작하면(정식·약칭 어느 쪽이든) 시도를 다시 붙이지 않는다(sweep L).
 *   ('경남', '경상남도 창원시 성산구 신촌동 …') → '경상남도 창원시 성산구 신촌동 …'   (종전 「경남 경상남도 …」)
 *   ('충남', '충청남도 아산시 탕정면 일원')      → '충청남도 아산시 탕정면 일원'
 *   ('부산', '해운대구 우동 1')                 → '부산 해운대구 우동 1'
 *   시군구를 넘기면, 주소에 시군구가 없을 때 시도 뒤에 끼운다('부산','남구','고동골로78번길 26' → '부산 남구 고동골로78번길 26').
 * 주소가 비면 joinPlace(시도·시군구) 로 떨어진다.
 */
export function addressWithRegion(region: string | null | undefined, address: string | null | undefined, sigungu?: string | null): string {
  const addr = String(address ?? '').replace(/\s+/g, ' ').trim();
  const sido = sidoShortName(region) ?? String(region ?? '').trim();
  if (!addr) return joinPlace({ sido, sigungu });
  const head = addr.split(' ')[0];
  // 시도 표기(정식·약칭)로 시작하면 그대로 — 다른 시도 표기여도 주소 쪽을 믿는다(앞에 또 붙이지 않는다).
  if (sidoShortName(head)) return addr;
  const sg = String(sigungu ?? '').trim();
  const withSg = sg && !addr.split(' ').some((t) => t === sg || t.startsWith(sg)) && !(sido && sidoShortName(sg) === sido) ? `${sg} ${addr}` : addr;
  return sido ? `${sido} ${withSg}` : withSg;
}

/**
 * 이름 옆 유형 배지 — 이름이 이미 그 유형어로 끝나면 배지를 내지 않는다(sweep S 「감만1 재개발 재개발」).
 *   ('감만1 재개발', '재개발') → null · ('감만1구역', '재개발') → '재개발' · ('센텀1구역 재건축(재송코오롱)', '재건축') → null
 */
export function typeBadgeFor(name: string | null | undefined, badge: string | null | undefined): string | null {
  const b = String(badge ?? '').trim();
  if (!b) return null;
  const core = String(name ?? '').trim().replace(/\s*[(（][^)）]*[)）]\s*$/, '').trim();
  if (core.endsWith(b)) return null;
  if (nameEndsWithTypeWord(name) && TAIL_LEADS_TYPE_RE.test(b)) return null;
  return b;
}

/**
 * seo-detail (2026-10-25) — 띄어쓰기만 다른 이름 표기 1개(「트리븐 센텀」 ↔ 「트리븐센텀」 — 네이버 검색량이 같은 크기로 따로 잡힌다).
 *   후보는 원장 name_variants 에서만(지어내지 않는다). 공백을 뺀 글자가 같고 문자열은 다른 것 중 공백이 가장 적은 것 · 없으면 null.
 */
export function spacingVariantOf(name: string | null | undefined, variants: unknown): string | null {
  const n = String(name ?? '').replace(/\s+/g, ' ').trim();
  if (!n || !Array.isArray(variants)) return null;
  const sq = (s: string) => s.replace(/\s+/g, '').normalize('NFC');
  const target = sq(n);
  let best: string | null = null;
  for (const v of variants) {
    if (typeof v !== 'string') continue;
    const t = v.replace(/\s+/g, ' ').trim();
    if (!t || t === n || sq(t) !== target) continue;
    if (best == null || (t.match(/ /g)?.length ?? 0) < (best.match(/ /g)?.length ?? 0)) best = t;
  }
  return best;
}
